/* =====================================================================
   Viscosity Labs · brand-rehaul/prototype
   buy-box.js — loaded on index.html only.

   Responsibilities:
     1. FOUNDER50 ?discount=FOUNDER50 detection (note already visible by
        default; this just confirms the URL state).
     2. Purchase-option active state (one-time vs disabled Subscribe).
     3. Buy actions (AJAX cart):
        - Every Add-to-Cart button (hero / sticky / final) POSTs to
          /cart/add.js, reads /cart.js, fills the slide-in cart sheet with
          the real line + subtotal, and opens the sheet — no page reload.
        - The sheet's Checkout button sends the shopper to
          /discount/FOUNDER50?redirect=/checkout so the code binds to the
          existing cart (no second add).
        - If the AJAX add fails for any reason we fall back to the live cart
          permalink so a sale is never lost.
     4. Sticky mobile buy bar: shown once the in-page ATC scrolls out of
        view, hidden again once the final CTA enters view, so the page
        never shows two live buy CTAs at once.
     5. Conversion tracking: fires Meta AddToCart + GA4 add_to_cart on a
        successful add, and Meta InitiateCheckout + GA4 begin_checkout at
        checkout (guarded, no-ops if pixel/gtag absent), so this page keeps
        the conversion signal the old homepage captured via its Lead event.
   ===================================================================== */

(() => {
  "use strict";

  /* ---------- 0. Config ---------- */
  const VARIANT_ID = 53272742560057;        // Skin Normalizer, single variant
  const DISCOUNT = "FOUNDER50";
  const CURRENCY = "USD";
  const FOUNDER_VALUE = 30.0;                // $60.00 list, 50% off via FOUNDER50
  /* Applies the code to the existing cart, then forwards to checkout. */
  const CHECKOUT_URL = "/discount/" + DISCOUNT + "?redirect=/checkout";
  /* Fallback only: the cart permalink adds the item AND applies the discount,
     used if the AJAX add ever fails so the shopper still reaches checkout. */
  const CART_URL = "https://viscositylabs.com/cart/" + VARIANT_ID + ":1?discount=" + DISCOUNT;

  /* ---------- 1. FOUNDER50 detection ---------- */
  try {
    const code = (new URLSearchParams(window.location.search).get("discount") || "").toUpperCase();
    if (code === DISCOUNT) {
      const applied = document.getElementById("vl-founder-applied");
      if (applied) applied.dataset.urlConfirmed = "true";
    }
  } catch (_) {}

  /* ---------- 2. Purchase-option active state ---------- */
  const radios = document.querySelectorAll('input[name="vl-purchase"]');
  radios.forEach((radio) => {
    radio.addEventListener("change", () => {
      document.querySelectorAll(".vl-option").forEach((opt) => {
        const r = opt.querySelector('input[name="vl-purchase"]');
        opt.classList.toggle("vl-option--active", !!r && r.checked && !r.disabled);
      });
    });
  });

  /* ---------- 3. Cart sheet ---------- */
  const sheet = document.getElementById("vl-cart-sheet");
  const scrim = document.getElementById("vl-cart-scrim");
  let lastFocus = null;
  let checkoutValue = FOUNDER_VALUE; // refreshed from the live cart on each add

  function openCart(trigger) {
    if (!sheet) return;
    lastFocus = trigger || document.activeElement;
    sheet.classList.add("is-open");
    sheet.setAttribute("aria-hidden", "false");
    if (scrim) scrim.classList.add("is-open");
    const close = document.getElementById("vl-cart-close");
    if (close) close.focus();
  }

  function closeCart() {
    if (!sheet) return;
    sheet.classList.remove("is-open");
    sheet.setAttribute("aria-hidden", "true");
    if (scrim) scrim.classList.remove("is-open");
    if (lastFocus && typeof lastFocus.focus === "function") {
      lastFocus.focus();
    }
  }

  /* ---------- 3a. Helpers ---------- */
  const money = (cents) => "$" + (Math.round(cents) / 100).toFixed(2);

  function setLoading(el, on) {
    if (!el) return;
    el.classList.toggle("is-loading", on);
    el.setAttribute("aria-busy", on ? "true" : "false");
    el.disabled = on;
  }

  /* Fill the sheet from a live /cart.js payload. The FOUNDER50 row is a
     preview (codes bind at checkout, not in cart.js) computed as 50% off the
     subtotal so it scales with quantity. */
  function updateSheet(cart) {
    if (!sheet || !cart) return;
    const items = cart.items || [];
    const line = items.find((i) => i.variant_id === VARIANT_ID) || items[0];
    const qty = line ? line.quantity : 1;
    const linePrice = line ? line.original_line_price : (cart.items_subtotal_price || 0);
    const subtotal = cart.items_subtotal_price != null ? cart.items_subtotal_price : (cart.total_price || 0);
    const discount = Math.round(subtotal * 0.5);
    const due = subtotal - discount;
    checkoutValue = due / 100;

    /* Update only the qty value + clamp state — never overwrite the whole
       .vl-cart-sheet__sub, which holds the stepper markup. */
    const qtyEl = sheet.querySelector("#vl-cart-qty");
    if (qtyEl) qtyEl.textContent = qty;
    const minusEl = sheet.querySelector("#vl-cart-qty-minus");
    if (minusEl) minusEl.disabled = qty <= 1;
    const priceEl = sheet.querySelector(".vl-cart-sheet__price");
    if (priceEl) priceEl.textContent = money(linePrice);

    sheet.querySelectorAll(".vl-cart-sheet__totals .vl-cart-sheet__row").forEach((row) => {
      const val = row.querySelector("span:last-child");
      if (!val) return;
      if (row.classList.contains("vl-cart-sheet__row--discount")) {
        val.innerHTML = "&minus;" + money(discount);
      } else if (!row.classList.contains("vl-cart-sheet__row--muted")) {
        val.textContent = money(subtotal);
      }
    });

    const coLabel = document.getElementById("vl-cart-checkout-label");
    if (coLabel) coLabel.textContent = "Checkout, " + money(due);
  }

  /* ---------- 3b. Conversion tracking (guarded) ---------- */
  function fireAddToCart() {
    try {
      if (typeof window.fbq !== "undefined") {
        window.fbq("track", "AddToCart", {
          value: FOUNDER_VALUE,
          currency: CURRENCY,
          content_name: "Skin Normalizer",
        });
      }
    } catch (_) {}
    try {
      if (typeof window.gtag !== "undefined") {
        window.gtag("event", "add_to_cart", {
          value: FOUNDER_VALUE,
          currency: CURRENCY,
          items: [{ item_name: "Skin Normalizer", quantity: 1, price: FOUNDER_VALUE }],
        });
      }
    } catch (_) {}
  }

  function fireCheckout() {
    try {
      if (typeof window.fbq !== "undefined") {
        window.fbq("track", "InitiateCheckout", {
          value: checkoutValue,
          currency: CURRENCY,
          content_name: "Skin Normalizer",
        });
      }
    } catch (_) {}
    try {
      if (typeof window.gtag !== "undefined") {
        window.gtag("event", "begin_checkout", {
          value: checkoutValue,
          currency: CURRENCY,
          transport_type: "beacon",
          items: [{ item_name: "Skin Normalizer", price: checkoutValue }],
        });
      }
    } catch (_) {}
  }

  /* ---------- 3c. Add to cart (AJAX) ---------- */
  async function addToCart(trigger) {
    setLoading(trigger, true);
    try {
      const addRes = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: VARIANT_ID, quantity: 1 }] }),
      });
      if (!addRes.ok) throw new Error("add.js " + addRes.status);
      fireAddToCart();
      const cartRes = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (cartRes.ok) updateSheet(await cartRes.json());
      openCart(trigger);
    } catch (_) {
      /* Never lose a sale — the permalink adds the item and applies FOUNDER50. */
      window.location.href = CART_URL;
      return;
    } finally {
      setLoading(trigger, false);
    }
  }

  function goToCheckout() {
    fireCheckout();
    window.location.href = CHECKOUT_URL;
  }

  ["vl-atc", "vl-sticky-atc", "vl-final-atc"].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener("click", (e) => {
      e.preventDefault();
      addToCart(el);
    });
  });
  const checkoutBtn = document.getElementById("vl-cart-checkout");
  if (checkoutBtn) checkoutBtn.addEventListener("click", (e) => {
    e.preventDefault();
    goToCheckout();
  });

  ["vl-cart-close", "vl-cart-continue"].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener("click", closeCart);
  });
  if (scrim) scrim.addEventListener("click", closeCart);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && sheet && sheet.classList.contains("is-open")) {
      closeCart();
    }
  });

  /* ---------- 3d. Quantity stepper (live cart) ---------- */
  const qtyMinus = document.getElementById("vl-cart-qty-minus");
  const qtyPlus = document.getElementById("vl-cart-qty-plus");
  const qtyVal = document.getElementById("vl-cart-qty");
  const readQty = () => parseInt(qtyVal && qtyVal.textContent, 10) || 1;
  let qtyBusy = false;

  /* Persist the change to the real cart, then re-render the sheet from the
     server response so totals stay correct. Buttons lock while in flight so
     rapid clicks can't desync the cart. */
  async function changeQty(newQty) {
    const next = Math.max(1, newQty); // clamp min to 1
    if (qtyBusy) return;
    qtyBusy = true;
    if (qtyMinus) qtyMinus.disabled = true;
    if (qtyPlus) qtyPlus.disabled = true;
    try {
      /* /cart/update.js keyed by variant_id is the reliable way to SET a
         quantity for a variant already in the cart, and it returns the full
         cart JSON. change.js identified by variant id can silently no-op once
         a discount is in play — that is why the −/+ appeared dead. */
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates: { [VARIANT_ID]: next } }),
      });
      if (!res.ok) throw new Error("update.js " + res.status);
      updateSheet(await res.json());
    } catch (err) {
      if (window.console && console.error) console.error("[vl cart] qty change failed:", err);
      /* Re-sync from the live cart so the UI matches server truth on failure. */
      try {
        const cartRes = await fetch("/cart.js", { headers: { Accept: "application/json" } });
        if (cartRes.ok) updateSheet(await cartRes.json());
      } catch (_) {}
    } finally {
      qtyBusy = false;
      if (qtyPlus) qtyPlus.disabled = false;
      if (qtyMinus) qtyMinus.disabled = readQty() <= 1;
    }
  }

  if (qtyMinus) qtyMinus.addEventListener("click", () => changeQty(readQty() - 1));
  if (qtyPlus) qtyPlus.addEventListener("click", () => changeQty(readQty() + 1));

  /* ---------- 4. Sticky mobile buy bar ---------- */
  const sticky = document.getElementById("vl-sticky-buy");
  const heroAtc = document.getElementById("vl-atc");
  const finalCta = document.getElementById("vl-final-cta");

  if (sticky && heroAtc && "IntersectionObserver" in window) {
    let atcVisible = true;
    let finalVisible = false;

    const sync = () => {
      const show = !atcVisible && !finalVisible;
      sticky.classList.toggle("is-visible", show);
      sticky.setAttribute("aria-hidden", show ? "false" : "true");
    };

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          atcVisible = entry.isIntersecting;
        });
        sync();
      },
      { rootMargin: "0px 0px -40px 0px" }
    ).observe(heroAtc);

    if (finalCta) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          finalVisible = entry.isIntersecting;
        });
        sync();
      }).observe(finalCta);
    }
  }
})();
